import { useLazyQuery } from "@apollo/client"
import {
  AssetEmissionsDocument,
  Emissions as EmissionsType,
  GetInstallationEmissionsDocument,
} from "@hydrologiq/gql/graphql"
import {
  IANATimeZone,
  endOfDay,
  formatYYYYMMDD,
  nowDateTime,
  startOfDay,
  toTimestamp,
  toUserTZ,
} from "@hydrologiq/common/helpers/date"
import { EnergyPeriod } from "@hydrologiq/common/types/energy/consumption"
import { DateTime } from "timezonecomplete"
import EmissionsFactor from "@hydrologiq/shared/monitoring/figures/EmissionsFactor"
import { useEffect } from "react"

export interface InstallationEmissionsFactorProps {
  installationEndDate: DateTime
  assetId?: string
  title?: string
  installationId?: string
}

function InstallationEmissionsFactor({
  installationEndDate,
  assetId,
  title,
  installationId,
}: InstallationEmissionsFactorProps) {
  const dateNow = nowDateTime()
  const endOfInstallation = dateNow.unixUtcMillis() > installationEndDate.unixUtcMillis()
  const endDate = endOfInstallation ? installationEndDate : dateNow
  const prevDay = endDate

  const [
    getAssetEmissions,
    { loading: getAssetEmissionsLoading, error: getAssetEmissionsError, data: assetEmissionsData },
  ] = useLazyQuery(AssetEmissionsDocument)
  const [
    getInstallationEmissions,
    { loading: getInstallationEmissionsLoading, error: getInstallationEmissionsError, data: installationEmissionsData },
  ] = useLazyQuery(GetInstallationEmissionsDocument)

  const baseVars = {
    start: toTimestamp(startOfDay(prevDay), true),
    end: toTimestamp(endOfDay(prevDay), true),
    location: IANATimeZone(),
    compare: true,
  }

  useEffect(() => {
    if (assetId) {
      getAssetEmissions({
        variables: {
          ...baseVars,
          asset: assetId,
        },
      })
    } else if (installationId) {
      getInstallationEmissions({
        variables: {
          ...baseVars,
          installation: installationId,
        },
      })
    }
  }, [assetId, installationId])

  const data = assetId ? assetEmissionsData : installationEmissionsData

  const dataPoint =
    (data &&
      data.values &&
      data.values.length > 0 &&
      data.values[data.values.length - 1] &&
      (data.values[data.values.length - 1] as EmissionsType)) ||
    undefined

  const loading = assetId ? getAssetEmissionsLoading : getInstallationEmissionsLoading
  const error = assetId ? getAssetEmissionsError : getInstallationEmissionsError

  // If the previous day time is today then we set true
  const today = dataPoint && dataPoint.start && formatYYYYMMDD(toUserTZ(dataPoint.start)) === formatYYYYMMDD(endDate)

  return (
    <EmissionsFactor
      title={title}
      loading={loading}
      emissions={dataPoint}
      period={today ? "today" : EnergyPeriod.PREV_DAY}
      error={error?.message}
    />
  )
}

export default InstallationEmissionsFactor
